// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { ReactNode } from 'react';
import clsx from 'clsx';

import { warnOnce } from '@amzn/awsui-component-toolkit/internal';

export type NativeAttributes<T extends React.HTMLAttributes<HTMLElement>> =
  | (Omit<T, 'children'> & Record<`data-${string}`, string>)
  | undefined;

export type SkipWarnings = boolean | string[];

type NativeAttributesProps<AT extends React.HTMLAttributes<HTMLElement>> = {
  tag: string;
  children?: ReactNode;
  skipWarnings?: SkipWarnings;
  nativeAttributes: NativeAttributes<AT>;
  componentName: string;
} & NativeAttributes<AT>;
interface ForwardRefType {
  <ET extends HTMLElement, AT extends React.HTMLAttributes<ET>>(
    props: NativeAttributesProps<AT> & { ref?: React.Ref<ET> }
  ): JSX.Element;
}

export function processAttributes<ET extends HTMLElement, AT extends React.HTMLAttributes<ET>>(
  rest: Omit<NativeAttributesProps<AT>, 'children' | 'tag' | 'skipWarnings' | 'componentName' | 'nativeAttributes'>,
  nativeAttributes: NativeAttributes<AT>,
  componentName: string,
  skipWarnings?: SkipWarnings
) {
  return Object.entries(nativeAttributes || {}).reduce(
    (acc, [key, value]) => {
      // concatenate className
      if (key === 'className') {
        acc[key] = clsx(rest.className, value);

        // merge style
      } else if (key === 'style') {
        acc[key] = { ...rest.style, ...value };

        // chain event handlers
      } else if (key.match(/^on[A-Z]/) && typeof value === 'function' && key in rest) {
        acc[key] = (event: Event) => {
          value(event);
          if (!event.defaultPrevented) {
            (rest as any)[key](event);
          }
        };

        // override other attributes, warning if it already exists
      } else {
        if (key in rest && (!skipWarnings || (skipWarnings !== true && !skipWarnings.includes(key)))) {
          warnOnce(componentName, `Overriding native attribute [${key}] which has a Cloudscape-provided value`);
        }
        acc[key] = value;
      }
      return acc;
    },
    { ...rest } as any
  );
}

export default React.forwardRef(
  <ET extends HTMLElement, AT extends React.HTMLAttributes<ET>>(
    { tag, nativeAttributes, children, skipWarnings, componentName, ...rest }: NativeAttributesProps<AT>,
    ref: React.Ref<ET>
  ) => {
    const Tag = tag;

    const processedAttributes = processAttributes<ET, AT>(rest, nativeAttributes, componentName, skipWarnings);

    return (
      <Tag {...processedAttributes} ref={ref}>
        {children}
      </Tag>
    );
  }
) as ForwardRefType;



// WEBPACK FOOTER //
// /local/home/vibhorbh/workplace/src/internal/utils/with-native-attributes.tsx